import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { getTelemetryCorrelationIds } from './correlation';
import type { FunnelEventName, TelemetryProperties } from './events';

let appInsights: ApplicationInsights | null = null;
let initAttempted = false;

export function trackFunnelEvent(name: FunnelEventName, properties: TelemetryProperties = {}): void {
  const client = getAppInsightsClient();
  if (!client) return;

  try {
    client.trackEvent({ name }, buildProperties(properties));
  } catch {
    // Telemetry must never break the app.
  }
}

export function trackException(error: unknown, properties: TelemetryProperties = {}): void {
  const client = getAppInsightsClient();
  if (!client) return;

  try {
    const exception = error instanceof Error ? error : new Error(String(error || 'Unknown error'));
    client.trackException({ exception, properties: buildProperties(properties) });
  } catch {
    // Telemetry must never break the app.
  }
}

function getAppInsightsClient(): ApplicationInsights | null {
  if (initAttempted) return appInsights;
  initAttempted = true;

  const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING?.trim();
  if (!connectionString) return null;

  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: false,
        disableAjaxTracking: false,
      },
    });
    appInsights.loadAppInsights();
    return appInsights;
  } catch {
    appInsights = null;
    return null;
  }
}

function buildProperties(properties: TelemetryProperties): Record<string, string | number | boolean> {
  return sanitizeProperties({
    ...getTelemetryCorrelationIds(),
    ...getBrowserProperties(),
    ...properties,
  });
}

function getBrowserProperties(): TelemetryProperties {
  if (typeof window === 'undefined') return {};

  const params = new URLSearchParams(window.location.search);
  return {
    route: `${window.location.pathname}${window.location.search}`,
    referrer: document.referrer || undefined,
    browserLanguage: navigator.language,
    screenWidth: window.screen?.width,
    screenHeight: window.screen?.height,
    utmSource: params.get('utm_source') || undefined,
    utmMedium: params.get('utm_medium') || undefined,
    utmCampaign: params.get('utm_campaign') || undefined,
    utmTerm: params.get('utm_term') || undefined,
    utmContent: params.get('utm_content') || undefined,
  };
}

function sanitizeProperties(properties: TelemetryProperties): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(properties)) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
    }
  }
  return out;
}
